import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Avatar,
  Switch,
  DatePicker,
  Row,
  Col,
  Statistic,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  LockOutlined,
  UnlockOutlined,
  EyeOutlined,
  ExportOutlined,
  ImportOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AccountManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [form] = Form.useForm();

  // 模拟账号数据
  const accountData = [
    {
      id: 1,
      username: 'admin',
      name: '超级管理员',
      role: 'super_admin',
      status: 'active',
      lastLogin: '2025-10-24 14:30:00',
      loginCount: 1250,
      createTime: '2025-01-01 10:00:00',
      avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=admin',
      googleAuth: true,
    },
    {
      id: 2,
      username: 'manager',
      name: '系统管理员',
      role: 'admin',
      status: 'active',
      lastLogin: '2025-10-24 13:45:00',
      loginCount: 890,
      createTime: '2025-01-15 09:30:00',
      avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=manager',
      googleAuth: true,
    },
    {
      id: 3,
      username: 'operator',
      name: '操作员',
      role: 'operator',
      status: 'active',
      lastLogin: '2025-10-24 12:20:00',
      loginCount: 456,
      createTime: '2025-02-01 14:20:00',
      avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=operator',
      googleAuth: false,
    },
    {
      id: 4,
      username: 'viewer',
      name: '查看者',
      role: 'viewer',
      status: 'inactive',
      lastLogin: '2025-10-20 16:10:00',
      loginCount: 123,
      createTime: '2025-03-01 11:15:00',
      avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=viewer',
      googleAuth: false,
    },
    {
      id: 5,
      username: 'test_user',
      name: '测试用户',
      role: 'operator',
      status: 'locked',
      lastLogin: '2025-10-18 10:30:00',
      loginCount: 67,
      createTime: '2025-04-01 15:45:00',
      avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=test',
      googleAuth: true,
    },
  ];

  const handleAddAccount = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  const handleEditAccount = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleViewDetail = (record: any) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleSaveAccount = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('账号更新成功！');
      } else {
        message.success('账号创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = (record: any) => {
    message.loading('正在删除账号...', 1);
    setTimeout(() => {
      message.success(`已删除账号 ${record.username}`);
    }, 1000);
  };

  const handleToggleStatus = (record: any) => {
    const newStatus = record.status === 'active' ? 'inactive' : 'active';
    message.loading('正在更新状态...', 1);
    setTimeout(() => {
      message.success(`账号 ${record.username} 已${newStatus === 'active' ? '启用' : '禁用'}`);
    }, 1000);
  };

  const handleUnlockAccount = (record: any) => {
    message.loading('正在解锁账号...', 1);
    setTimeout(() => {
      message.success(`账号 ${record.username} 已解锁`);
    }, 1000);
  };

  const handleResetPassword = (record: any) => {
    message.loading('正在重置密码...', 1);
    setTimeout(() => {
      message.success(`账号 ${record.username} 密码已重置为默认密码`);
    }, 1000);
  };

  const handleToggleGoogleAuth = (record: any) => {
    const newStatus = !record.googleAuth;
    message.loading('正在更新谷歌验证码设置...', 1);
    setTimeout(() => {
      message.success(`账号 ${record.username} 谷歌验证码已${newStatus ? '开启' : '关闭'}`);
    }, 1000);
  };

  const handleResetGoogleAuth = (record: any) => {
    message.loading('正在重置谷歌验证码...', 1);
    setTimeout(() => {
      message.success(`账号 ${record.username} 谷歌验证码已重置`);
    }, 1000);
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的账号');
      return;
    }
    message.loading('正在批量删除...', 1);
    setTimeout(() => {
      message.success(`已删除 ${selectedRowKeys.length} 个账号`);
      setSelectedRowKeys([]);
    }, 1000);
  };

  const handleExport = () => {
    message.loading('正在导出数据...', 1);
    setTimeout(() => {
      message.success('账号数据导出成功！');
    }, 1000);
  };

  const handleImport = () => {
    message.loading('正在导入数据...', 1);
    setTimeout(() => {
      message.success('账号数据导入成功！');
    }, 1000);
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      super_admin: 'red',
      admin: 'blue',
      manager: 'green',
      operator: 'orange',
      viewer: 'default',
    };
    return colors[role] || 'default';
  };

  const getRoleName = (role: string) => {
    const names: Record<string, string> = {
      super_admin: '超级管理员',
      admin: '系统管理员',
      manager: '业务管理员',
      operator: '操作员',
      viewer: '查看者',
    };
    return names[role] || role;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'default',
      locked: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusName = (status: string) => {
    const names: Record<string, string> = {
      active: '正常',
      inactive: '禁用',
      locked: '锁定',
    };
    return names[status] || status;
  };

  const columns = [
    {
      title: '用户信息',
      key: 'userInfo',
      width: 250,
      fixed: 'left' as const,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar src={record.avatar} icon={<UserOutlined />} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.username}</div>
          </div>
        </div>
      ),
    },
    {
      title: '角色权限',
      key: 'role',
      width: 120,
      render: (_: any, record: any) => (
        <Tag color={getRoleColor(record.role)}>
          {getRoleName(record.role)}
        </Tag>
      ),
    },
    {
      title: '谷歌验证码',
      key: 'googleAuth',
      width: 120,
      render: (_: any, record: any) => (
        <Tag color={record.googleAuth ? 'green' : 'default'}>
          {record.googleAuth ? '已开启' : '未开启'}
        </Tag>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_: any, record: any) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusName(record.status)}
        </Tag>
      ),
    },
    {
      title: '登录统计',
      key: 'loginStats',
      width: 120,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontSize: 12 }}>登录次数: {record.loginCount}</div>
          <div style={{ fontSize: 12, color: '#666' }}>最后登录: {record.lastLogin}</div>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_: any, record: any) => (
        <Space wrap>
          <Tooltip title="查看详情">
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
              详情
            </Button>
          </Tooltip>
          <Tooltip title="编辑账号">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEditAccount(record)}>
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="重置密码">
            <Button type="link" icon={<LockOutlined />} onClick={() => handleResetPassword(record)}>
              重置密码
            </Button>
          </Tooltip>
          <Tooltip title={record.googleAuth ? '关闭谷歌验证码' : '开启谷歌验证码'}>
            <Button 
              type="link" 
              onClick={() => handleToggleGoogleAuth(record)}
            >
              {record.googleAuth ? '关闭验证码' : '开启验证码'}
            </Button>
          </Tooltip>
          <Tooltip title="重置谷歌验证码">
            <Button type="link" onClick={() => handleResetGoogleAuth(record)}>
              重置验证码
            </Button>
          </Tooltip>
          {record.status === 'locked' ? (
            <Tooltip title="解锁账号">
              <Button type="link" icon={<UnlockOutlined />} onClick={() => handleUnlockAccount(record)}>
                解锁
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title={record.status === 'active' ? '禁用账号' : '启用账号'}>
              <Button 
                type="link" 
                icon={record.status === 'active' ? <LockOutlined /> : <UnlockOutlined />}
                onClick={() => handleToggleStatus(record)}
              >
                {record.status === 'active' ? '禁用' : '启用'}
              </Button>
            </Tooltip>
          )}
          <Popconfirm
            title="确定要删除这个账号吗？"
            onConfirm={() => handleDeleteAccount(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除账号">
              <Button type="link" icon={<DeleteOutlined />} danger>删除</Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredData = accountData.filter(item => {
    const matchesSearch = !searchText || 
      item.username.toLowerCase().includes(searchText.toLowerCase()) ||
      item.name.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesRole = filterRole === 'all' || item.role === filterRole;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总账号数" value={accountData.length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="活跃账号" value={accountData.filter(item => item.status === 'active').length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="锁定账号" value={accountData.filter(item => item.status === 'locked').length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="今日登录" value={Math.floor(Math.random() * 50) + 20} />
          </Card>
        </Col>
      </Row>

      <Card
        title="账号管理"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => message.info('数据已刷新')}
            >
              刷新
            </Button>
            <Button
              icon={<ImportOutlined />}
              onClick={handleImport}
            >
              导入
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              导出
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAccount}
            >
              新增账号
            </Button>
          </Space>
        }
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="搜索用户名或姓名"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 120 }}
            >
              <Option value="all">全部状态</Option>
              <Option value="active">正常</Option>
              <Option value="inactive">禁用</Option>
              <Option value="locked">锁定</Option>
            </Select>
            <Select
              value={filterRole}
              onChange={setFilterRole}
              style={{ width: 120 }}
            >
              <Option value="all">全部角色</Option>
              <Option value="super_admin">超级管理员</Option>
              <Option value="admin">系统管理员</Option>
              <Option value="manager">业务管理员</Option>
              <Option value="operator">操作员</Option>
              <Option value="viewer">查看者</Option>
            </Select>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个账号吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1400 }}
          size="middle"
        />
      </Card>

      {/* 编辑账号模态框 */}
      <Modal
        title={selectedRecord ? '编辑账号' : '新增账号'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveAccount}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select>
                  <Option value="super_admin">超级管理员</Option>
                  <Option value="admin">系统管理员</Option>
                  <Option value="manager">业务管理员</Option>
                  <Option value="operator">操作员</Option>
                  <Option value="viewer">查看者</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Option value="active">正常</Option>
                  <Option value="inactive">禁用</Option>
                  <Option value="locked">锁定</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="googleAuth"
            label="谷歌验证码"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>

          {!selectedRecord && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6位' }
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 账号详情模态框 */}
      <Modal
        title="账号详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar size={80} src={selectedRecord.avatar} icon={<UserOutlined />} />
                  <div style={{ marginTop: 8, fontWeight: 'bold' }}>{selectedRecord.name}</div>
                  <div style={{ color: '#666' }}>{selectedRecord.username}</div>
                </div>
              </Col>
              <Col span={16}>
                <div style={{ marginBottom: 16 }}>
                  <h4>权限信息</h4>
                  <p><strong>角色:</strong> <Tag color={getRoleColor(selectedRecord.role)}>{getRoleName(selectedRecord.role)}</Tag></p>
                  <p><strong>状态:</strong> <Tag color={getStatusColor(selectedRecord.status)}>{getStatusName(selectedRecord.status)}</Tag></p>
                  <p><strong>谷歌验证码:</strong> <Tag color={selectedRecord.googleAuth ? 'green' : 'default'}>{selectedRecord.googleAuth ? '已开启' : '未开启'}</Tag></p>
                </div>

                <div>
                  <h4>登录统计</h4>
                  <p><strong>登录次数:</strong> {selectedRecord.loginCount}</p>
                  <p><strong>最后登录:</strong> {selectedRecord.lastLogin}</p>
                  <p><strong>创建时间:</strong> {selectedRecord.createTime}</p>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountManagement;
